/**
 * Step3Expenses — Phase 27 (was "Step 4" pre-refactor).
 *
 * Operating expenses: taxes, insurance, HOA, utilities, vacancy %, mgmt %,
 * upfront repairs. Plus the Phase 17 tax-source pill (ATTOM / county /
 * national-median / manual).
 *
 * Verbatim port from DealWizard.jsx renderStep step === 4 branch.
 * Preserve as-is: the tax-source pill is keyed by TAX_SOURCE_LABEL — adding
 * a new source string requires extending that constant too.
 */
import { Database, Pencil } from "lucide-react";
import { NumberField, TAX_SOURCE_LABEL } from "./_shared";
// Phase 44 Batch BF1 — multi-year special assessments below HOA.
import SpecialAssessmentsField from "@/components/wizard/SpecialAssessmentsField";

export default function Step3Expenses({ wizard }) {
  const { form, setAnalysis, taxSource, taxSourceMeta } = wizard;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Operating expenses</h2>
        <p className="mt-1 text-sm text-slate-500">Anything you'll pay every year (or month) to hold this property.</p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <NumberField id="taxes" label="Property taxes" prefix="$" suffix="/yr"
            value={form.analysis.taxes_annual}
            onChange={(v) => setAnalysis("taxes_annual", v)}
            testid="analysis-taxes" />
          {/* Phase 17 — visual label showing where the tax estimate came
              from. Cleared the moment the user edits the field. */}
          <div
            data-testid="tax-source-label"
            data-tax-source={taxSource || "manual"}
            className={`mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
              taxSource
                ? (TAX_SOURCE_LABEL[taxSource]?.tone === "indigo"
                    ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                    : "bg-amber-50 text-amber-800 border border-amber-200")
                : "bg-slate-50 text-slate-500 border border-slate-200"
            }`}
          >
            {taxSource ? (
              <>
                <Database className="h-3 w-3" />
                {TAX_SOURCE_LABEL[taxSource]?.label || `Estimated from ${taxSource}`}
                {taxSourceMeta?.rate_pct ? ` (${taxSourceMeta.rate_pct}%)` : ""}
              </>
            ) : (
              <>
                <Pencil className="h-3 w-3" />
                Manual entry
              </>
            )}
          </div>
        </div>
        <NumberField id="ins" label="Insurance" prefix="$" suffix="/yr"
          value={form.analysis.insurance_annual}
          onChange={(v) => setAnalysis("insurance_annual", v)}
          testid="analysis-insurance" />
        <NumberField id="hoa" label="HOA" prefix="$" suffix="/mo"
          value={form.analysis.hoa_monthly}
          onChange={(v) => setAnalysis("hoa_monthly", v)}
          testid="analysis-hoa" />
        <NumberField id="utils" label="Utilities" prefix="$" suffix="/mo"
          value={form.analysis.utilities_monthly}
          onChange={(v) => setAnalysis("utilities_monthly", v)}
          testid="analysis-utilities" />
        <NumberField id="vac" label="Vacancy" suffix="%"
          value={form.analysis.vacancy_percent}
          onChange={(v) => setAnalysis("vacancy_percent", v)}
          testid="analysis-vacancy" min={0} max={100}
          hint="Industry standard: ~5%." />
        <NumberField id="mgmt" label="Property management" suffix="%"
          value={form.analysis.management_percent}
          onChange={(v) => setAnalysis("management_percent", v)}
          testid="analysis-mgmt" min={0} max={100}
          hint="0% if you self-manage." />
        <NumberField id="repairs" label="Upfront repairs / make-ready" prefix="$"
          value={form.analysis.repairs}
          onChange={(v) => setAnalysis("repairs", v)}
          testid="analysis-repairs" />
        {/* Phase 44 Batch BF1 — multi-year special assessments. Spans
            the full grid so the card array reads as a distinct block
            below the simple expense fields. */}
        <SpecialAssessmentsField
          value={form.analysis.special_assessments}
          onChange={(v) => setAnalysis("special_assessments", v)}
        />
      </div>
    </div>
  );
}
